import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';

import { ActionDispatcherService } from '@core/services/domanda-action-handler/action-dispatcher.service';
import type { ActionKey } from '@core/services/domanda-action-handler/config/action-keys';
import { ActionEvent } from '@core/services/domanda-action-handler/models/action.models';
import { Subscription } from 'rxjs';

export interface BackgroundProcess {
  id: string; // formato: `${ActionKey}-${domandaId}`
  label: string;
  status: 'running' | 'error' | 'completed'; // Stato avanzato del processo
}

/**
 * Service per il monitoraggio dei processi di background.
 *
 * - Tiene traccia dei processi attivi tramite uno stato reattivo (signal).
 * - Espone la lista dei processi e il conteggio tramite computed.
 * - I processi vengono aggiunti/aggiornati tramite addOrUpdateProcess.
 * - Se un processo va in errore, viene marcato con error: true e non viene rimosso automaticamente.
 * - I processi in errore possono essere rimossi solo manualmente dall'utente.
 * - Badge e icone cambiano in base allo stato di errore del processo.
 *
 * Utilizzo tipico:
 * - Sottoscrizione automatica agli eventi di ActionDispatcherService.
 * - UI può mostrare badge, icone e pulsante di rimozione per i processi in errore.
 */
@Injectable({ providedIn: 'root' })
export class ProcessMonitorService implements OnDestroy {
  private readonly _processes = signal<BackgroundProcess[]>([]);
  readonly processes = computed(() => this._processes());

  private readonly dispatcher = inject(ActionDispatcherService);
  private dispatcherSubscription: Subscription | undefined;

  constructor() {
    this.dispatcherSubscription = this.dispatcher.event$.subscribe(
      (event: ActionEvent & { actionKey?: ActionKey; type?: string }) => {
        const actionKey: ActionKey | 'action' = event.actionKey ?? 'action';
        const { context } = event;
        const processId = `${actionKey}-${context.payload?.id ?? ''}`;

        if (context.status === 'aborted') {
          this.remove(processId);
          return;
        }

        if (context.status === 'in-progress') {
          this.addOrUpdateProcess({
            id: processId,
            label: context.message,
            status: 'running',
          });
        }

        if (context.status === 'error') {
          this.addOrUpdateProcess({
            id: processId,
            label: context.message,
            status: 'error',
          });
        }

        if (context.status === 'completed') {
          const proc = this._processes().find(p => p.id === processId);
          if (proc && proc.status !== 'error') {
            this.addOrUpdateProcess({
              id: processId,
              label: context.message || `${processId}: Completato`,
              status: 'completed',
            });
          }
        }
      }
    );
  }

  /**
   * Aggiunge un nuovo processo o aggiorna quello esistente (match su id).
   * Mantiene solo gli ultimi 5 processi completati e tutti quelli in errore.
   * @param process Processo da aggiungere o aggiornare
   */
  addOrUpdateProcess = (process: BackgroundProcess) => {
    this._processes.update(list => {
      const exists = list.some(p => p.id === process.id);
      const updated = exists
        ? list.map(p => (p.id === process.id ? { ...p, ...process } : p))
        : [...list, process];

      // Filtra: mantieni tutti gli error, tutti i running, e solo gli ultimi 5 completed
      const completed = updated.filter(p => p.status === 'completed');
      const errors = updated.filter(p => p.status === 'error');
      const running = updated.filter(p => p.status === 'running');
      // Prendi solo gli ultimi 5 completed (in base all'ordine di inserimento)
      const last5Completed = completed.slice(-5);
      return [...running, ...errors, ...last5Completed];
    });
  };

  /**
   * Rimuove un processo dalla lista tramite id.
   * @param processId id del processo da rimuovere
   */
  remove = (processId: string) =>
    this._processes.update(list => list.filter(p => p.id !== processId));

  /**
   * Rimuove tutti i processi dalla lista.
   */
  clear = () => this._processes.set([]);

  // Espone come pubbliche le computed per la UI
  public hasRunning = computed(() => this._processes().some(p => p.status === 'running'));

  public hasOnlyError = computed(
    () =>
      this._processes().some(p => p.status === 'error') &&
      !this._processes().some(p => p.status === 'running')
  );

  ngOnDestroy(): void {
    this.dispatcherSubscription?.unsubscribe();
    this.clear();
  }
}
